import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOB_TYPE_NAMES } from "@/utils/jobHelpers";

/**
 * Props for the JobTypeFilter component.
 */
interface JobTypeFilterProps {
  /** Currently selected job type, or null for "All Types" */
  value: string | null;
  /** Callback when selection changes */
  onChange: (value: string | null) => void;
}

/**
 * Job type options for the filter dropdown.
 * Maps internal job type values to human-readable labels.
 */
const JOB_TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "content_update", label: JOB_TYPE_NAMES.content_update },
  { value: "quality_check", label: JOB_TYPE_NAMES.quality_check },
  { value: "auto_fix", label: JOB_TYPE_NAMES.auto_fix },
  { value: "cleanup", label: JOB_TYPE_NAMES.cleanup },
  { value: "crawl_documentation", label: JOB_TYPE_NAMES.crawl_documentation },
  { value: "document_process", label: JOB_TYPE_NAMES.document_process },
  { value: "export", label: JOB_TYPE_NAMES.export },
];

/**
 * A dropdown filter component for filtering jobs by type.
 * Uses shadcn/ui Select component.
 *
 * @example
 * ```tsx
 * const [filter, setFilter] = useState<string | null>(null);
 * <JobTypeFilter value={filter} onChange={setFilter} />
 * ```
 */
export function JobTypeFilter({ value, onChange }: JobTypeFilterProps) {
  const handleValueChange = (newValue: string) => {
    // Convert "all" back to null for the parent component
    onChange(newValue === "all" ? null : newValue);
  };

  // Find the label for the current value
  const currentOption = JOB_TYPE_OPTIONS.find((opt) => opt.value === (value ?? "all"));

  return (
    <Select value={value ?? "all"} onValueChange={handleValueChange}>
      <SelectTrigger className="w-[180px]" data-testid="job-type-filter">
        <SelectValue placeholder="All Types">{currentOption?.label ?? "All Types"}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {JOB_TYPE_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
